import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { toast } from "react-toastify";

interface User {
  _id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: "client" | "developer" | "admin";
  profileImage?: string;
  isEmailVerified: boolean;
  skills?: string[];
  experience?: string;
  hourlyRate?: {
    amount: number;
    currency: string;
  };
  token?: string;
  availability?: string;
  preferences?: {
    notifications: {
      email: boolean;
      push: boolean;
    };
    language: string;
    timezone: string;
  };
  lastActive?: string;
  isOnline?: boolean;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (
    email: string,
    password: string
  ) => Promise<{ userId: string; requireOtp: boolean }>;
  register: (userData: RegisterData) => Promise<void>;
  verifyOtp: (
    userId: string,
    otp: string
  ) => Promise<{ user: User; accessToken: string; refreshToken: string }>;
  resendOtp: (userId: string) => Promise<void>;
  logout: () => void;
  refreshToken: () => Promise<boolean>;
  updateUser: (userData: Partial<User>) => void;
  syncAuth: () => Promise<void>;
  refreshUser: () => Promise<void>;
  forceAuthRefresh: () => Promise<void>;
}

interface RegisterData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  role: "client" | "developer";
  phone?: string;
  location?: string;
  bio?: string;
  skills?: string[];
  experience?: string;
  hourlyRate?: {
    amount: number;
    currency: string;
  };
  availability?: string;
  education?: any[];
  certifications?: any[];
  documents?: {
    name: string;
    type: string;
    file: File;
  }[];
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

// Get the correct API base URL based on environment
const getApiBaseUrl = (): string => {
  if (typeof window !== "undefined") {
    const hostname = window.location.hostname;
    const protocol = window.location.protocol;

    // Special handling for WebContainer preview URLs
    if (
      hostname.includes("webcontainer.io") ||
      hostname.includes("webcontainer-api.io")
    ) {
      const baseUrl = hostname.replace("--5173--", "--5002--");
      return `${protocol}//${baseUrl}/api`;
    }

    // Local development
    if (hostname === "localhost" || hostname === "127.0.0.1") {
      return `${protocol}//${hostname}:5002/api`;
    }

    // Render or other deployment domains
    return `${protocol}//${hostname}/api`;
  }

  // Fallback to env or default
  return import.meta.env.VITE_API_URL || "http://localhost:5002/api";
};

const API_BASE_URL = getApiBaseUrl();

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    initializeAuth();
  }, []);

  const initializeAuth = async () => {
    try {
      const storedUser = localStorage.getItem("user");
      const accessToken = localStorage.getItem("accessToken");

      if (storedUser && accessToken) {
        setUser(JSON.parse(storedUser));
        const verified = await verifyToken();
        if (!verified) {
          logout();
        }
      }
    } catch (error) {
      localStorage.clear();
    } finally {
      setLoading(false);
    }
  };

  const refreshToken = async (): Promise<boolean> => {
    try {
      const refreshTokenValue = localStorage.getItem("refreshToken");
      if (!refreshTokenValue) {
        throw new Error("No refresh token available");
      }

      const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken: refreshTokenValue }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Token refresh failed");
      }

      localStorage.setItem("accessToken", data.accessToken);
      localStorage.setItem("refreshToken", data.refreshToken);
      return true;
    } catch (error) {
      logout();
      return false;
    }
  };

  const verifyToken = useCallback(async () => {
    try {
      const token = localStorage.getItem("accessToken");
      if (!token) throw new Error("No authentication token found");

      const response = await fetch(`${API_BASE_URL}/auth/me`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const refreshed = await refreshToken();
        if (!refreshed) {
          return false; // Return false instead of throwing error
        }
        return true;
      }

      const data = await response.json();
      setUser(data.user);
      localStorage.setItem("user", JSON.stringify(data.user));
      return true;
    } catch (error) {
      // Don't call logout immediately, let the caller decide
      return false;
    }
  }, [refreshToken]);

  const login = async (email: string, password: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Login failed");
      }

      if (data.requireOtp) {
        return { userId: data.userId, requireOtp: true };
      }

      setUser(data.user);
      localStorage.setItem("user", JSON.stringify(data.user));
      localStorage.setItem("accessToken", data.accessToken);
      localStorage.setItem("refreshToken", data.refreshToken);

      toast.success("Login successful!");
      return { userId: data.user._id, requireOtp: false };
    } catch (error: any) {
      toast.error(error.message || "Login failed");
      throw error;
    }
  };

  const register = async (userData: RegisterData) => {
    try {
      let body: string | FormData;
      let headers: Record<string, string> = {};

      if (userData.role === "developer" && userData.documents?.length) {
        const formData = new FormData();
        Object.entries(userData).forEach(([key, value]) => {
          if (key === "documents") {
            value.forEach((doc: any) => {
              formData.append(`documents`, doc.file);
              formData.append(`documentNames`, doc.name);
              formData.append(`documentTypes`, doc.type);
            });
          } else {
            formData.append(
              key,
              typeof value === "object" ? JSON.stringify(value) : String(value)
            );
          }
        });
        body = formData;
      } else {
        body = JSON.stringify(userData);
        headers["Content-Type"] = "application/json";
      }

      const response = await fetch(`${API_BASE_URL}/auth/register`, {
        method: "POST",
        headers,
        body,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Registration failed");
      }

      setUser(data.user);
      localStorage.setItem("user", JSON.stringify(data.user));
      localStorage.setItem("accessToken", data.accessToken);
      localStorage.setItem("refreshToken", data.refreshToken);

      toast.success(
        "Registration successful! Please check your email to verify your account."
      );
    } catch (error: any) {
      toast.error(error.message || "Registration failed");
      throw error;
    }
  };

  const verifyOtp = async (userId: string, otp: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/verify-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, otp }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "OTP verification failed");
      }

      setUser(data.user);
      localStorage.setItem("user", JSON.stringify(data.user));
      localStorage.setItem("accessToken", data.accessToken);
      localStorage.setItem("refreshToken", data.refreshToken);

      toast.success("OTP verified successfully!");
      return {
        user: data.user,
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
      };
    } catch (error: any) {
      toast.error(error.message || "OTP verification failed");
      throw error;
    }
  };

  const resendOtp = async (userId: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/resend-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to resend OTP");
      }

      toast.success("OTP resent successfully!");
    } catch (error: any) {
      toast.error(error.message || "Failed to resend OTP");
      throw error;
    }
  };

  const logout = useCallback(async () => {
    try {
      const token = localStorage.getItem("accessToken");
      if (token) {
        await fetch(`${API_BASE_URL}/auth/logout`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
      }
    } catch (error) {
      // Error during logout
    } finally {
      setUser(null);
      localStorage.clear();
      toast.info("You have been logged out");
    }
  }, []);

  const updateUser = useCallback((userData: Partial<User>) => {
    setUser((prev) => {
      if (!prev) return null;
      const updated = { ...prev, ...userData };
      localStorage.setItem("user", JSON.stringify(updated));
      return updated;
    });
  }, []);

  const syncAuth = useCallback(async () => {
    try {
      const verified = await verifyToken();
      if (!verified) {
        logout();
      }
    } catch (error) {
      // Auth sync error
    }
  }, [verifyToken, logout]);

  const forceAuthRefresh = useCallback(async () => {
    try {
      // Just refresh the token without calling verifyToken
      const refreshed = await refreshToken();
      if (refreshed) {
        // Update user state from localStorage
        const storedUser = localStorage.getItem("user");
        if (storedUser) {
          setUser(JSON.parse(storedUser));
        }
      }
    } catch (error) {
      // Force auth refresh error
    }
  }, [refreshToken]);

  const value: AuthContextType = {
    user,
    loading,
    login,
    register,
    verifyOtp,
    resendOtp,
    logout,
    refreshToken,
    updateUser,
    syncAuth,
    refreshUser: syncAuth,
    forceAuthRefresh,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
